import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { Component, ErrorInfo, ReactNode } from "react";
import { CdnIcon } from "./CdnIcon";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackDescription?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("Error caught by ErrorBoundary:", error, errorInfo);
    }

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const {
        fallbackTitle = "Something went wrong",
        fallbackDescription = "An unexpected error occurred. Please try refreshing the page.",
      } = this.props;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 400,
            p: 4,
            textAlign: "center",
          }}
        >
          <CdnIcon
            name="BugIcon"
            sx={{
              fontSize: 64,
              color: "error.main",
              opacity: 0.5,
              mb: 2,
            }}
          />

          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
            {fallbackTitle}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 600 }}
          >
            {fallbackDescription}
          </Typography>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <Alert
              severity="error"
              sx={{ mb: 3, maxWidth: 600, textAlign: "left" }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Error Details:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
              >
                {this.state.error.message}
              </Typography>
              {this.state.errorInfo && (
                <details style={{ marginTop: "8px" }}>
                  <summary style={{ cursor: "pointer" }}>
                    Component Stack
                  </summary>
                  <pre style={{ fontSize: "0.75rem", overflow: "auto" }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CdnIcon name="RefreshIcon" />}
              onClick={this.handleRefresh}
            >
              Refresh Page
            </Button>
            <Button variant="outlined" onClick={this.handleReset}>
              Try Again
            </Button>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}
